import {
  formatNumber,
  formatNumberDigits,
  isArabicLanguage,
  toArabicIndicDigitsInString,
  wrapBidiIsolate,
} from "src/utils/numberFormat";

export const formatCompactNumber = (value: number, locale = "en"): string => {
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
};

export const formatPercent = (value: number, digits = 1): string =>
  `${value.toFixed(digits)}%`;

export const formatTooltipCount = (value: unknown, language: string): string =>
  formatNumber(Number(value ?? 0), {}, language);

export const formatTooltipPercent = (value: unknown, language: string, digits = 1): string => {
  const token = `${formatNumberDigits(
    Number(value ?? 0),
    { minimumFractionDigits: digits, maximumFractionDigits: digits },
    language,
  )}%`;
  return isArabicLanguage(language) ? wrapBidiIsolate(token) : token;
};

export const formatTooltipCompact = (value: unknown, language: string): string => {
  const formatted = formatCompactNumber(Number(value ?? 0), "en");
  if (!isArabicLanguage(language)) {
    return formatted;
  }
  return wrapBidiIsolate(toArabicIndicDigitsInString(formatted));
};

export const formatAxisPercent = (value: unknown): string =>
  `${Number(value ?? 0)}%`;

export const formatAxisCompact = (value: unknown, locale = "en"): string =>
  formatCompactNumber(Number(value ?? 0), locale);

export const formatShortDate = (value: string, locale = "en"): string => {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
  }).format(date);
};

export const formatTooltipDate = (value: string, language: string): string => {
  const formatted = formatShortDate(value, "en");
  if (!isArabicLanguage(language)) {
    return formatted;
  }
  return wrapBidiIsolate(toArabicIndicDigitsInString(formatted));
};

export const truncateLabel = (
  value: string | number | null | undefined,
  maxLength = 14,
): string => {
  const label = String(value ?? "").trim();
  if (label.length <= maxLength) {
    return label;
  }
  return `${label.slice(0, Math.max(1, maxLength - 1))}...`;
};
